import { useRef, useState } from 'react'

import { Form, message } from 'antd';
import bigDecimal from 'js-big-decimal';
import { useRouter } from 'next/router';

import { SpotTradeAPI } from '@api/spotTrade';
import { changeMargin as fetchChangeMargin } from '@api/trading'
import { disabledOpenMargin, getIsTouchDisabledModal, openMargin, setIsTouchDisabledModal } from '@components/index';
import { PRECISION } from '@constants/index'
import { useIsomorphicLayoutEffect } from '@lib/hooks'
import pubsub from '@lib/scripts/pubsub'
import helper from '@utils/helper';
import { getLoginPath, I18n } from '@utils/index'

export const useBaseForm = (params) => {
  const [loading, setLoading] = useState(false);
  const router = useRouter();
  const tradingPair = useRef(null);
  const {
    coinDetail,
    currentInputFocus,
    priceKey,
    isLimit,
    isMarket,
    marginBalance,
    fetchMarginBalance,
    close,
    tradeStore
  } = params;
  const [form] = Form.useForm();

  const updateBaseQty = ({ value, price }) => {
    const baseQty = helper.fixNumString(value || '', coinDetail?.tradeInputPrecision);
    let orderTotal = '';
    if (baseQty) {
      orderTotal = helper.formatFixNumString(price * parseFloat(baseQty), coinDetail?.tradeVolumePrecision)
    }
    form.setFieldsValue({ baseQty, orderTotal })
  }

  const updateOrderTotal = ({ value, price }) => {
    const orderTotal = helper.fixNumString(value || '', coinDetail?.tradeVolumePrecision);

    let baseQty = ''
    if (orderTotal && price) {
      baseQty = helper.formatFixNumString(parseFloat(orderTotal) / price, coinDetail?.tradeInputPrecision)
    }

    form.setFieldsValue({ baseQty, orderTotal })
  }

  const updatePrice = ({ value }) => {
    const price = helper.fixNumString(value, coinDetail?.tradePrecision ?? PRECISION);
    // 获取购买量 S
    const baseQty = form.getFieldValue('baseQty')
    // 获取购买量 E
    let orderTotal = ''
    try {
      orderTotal = (parseFloat(value || 0) * parseFloat(baseQty || 0)).toFixed(Number(coinDetail?.tradePrecision))
    } catch (e) {
      orderTotal = (parseFloat(value || 0) * parseFloat(baseQty || 0)).toString()
    }
    if (!price || !baseQty) {
      orderTotal = ''
    }
    form.setFieldsValue({ price, orderTotal })
  }

  const updateSlider = ({ maxAvail, price, value }) => {
    const percent = value / 100

    const orderAmount = bigDecimal.multiply(Number(maxAvail), Number(percent ?? 1));

    const baseQty = helper.formatFixNumString(orderAmount, coinDetail?.tradeInputPrecision);
    let orderTotal = ''
    if (orderAmount) {
      orderTotal = helper.formatFixNumString(price * parseFloat(orderAmount), coinDetail?.tradePrecision)
    }
    form.setFieldsValue({ baseQty, orderTotal, process: value })
  }

  const handleBaseQtyAndOrderTotal = () => {
    const { baseQty, orderTotal } = form.getFieldsValue()
    if (!currentInputFocus || (!baseQty && !orderTotal) || !priceKey || !isMarket) {
      return
    }
    if (currentInputFocus === 'orderTotal') {
      updateOrderTotal({ value: orderTotal, price: coinDetail[priceKey] })
    }
    if (currentInputFocus === 'baseQty') {
      updateBaseQty({ value: baseQty, price: coinDetail[priceKey] })
    }
  }

  const handleMarginDisabled = ({ spotAvbl, maxAvbl }) => {
    if (Number(spotAvbl) > Number(maxAvbl) && getIsTouchDisabledModal()) {
      return true
    }
    return false
  }

  useIsomorphicLayoutEffect(() => {
    handleBaseQtyAndOrderTotal()
  }, [currentInputFocus, coinDetail, isMarket])

  const initPrice = () => {
    if ((!isLimit || form.isFieldTouched('price') || form.getFieldValue('price') || !priceKey || !coinDetail?.[priceKey]) &&
    tradingPair.current && tradingPair.current === router.query?.coinCode) {
      return
    }
    tradingPair.current = router.query?.coinCode;
    form.resetFields()
    form.setFieldsValue({ price: coinDetail?.[priceKey]?.toFixed(Number(coinDetail.tradePrecision ?? PRECISION)) })
  }

  // 开启杠杆
  const changeMargin = async (opt) => {
    // app 有登录判断，这里不需要，如果没有登录点击杠杆直接跳转登录页面，这里不需要特殊处理
    let params = {}
    if (typeof opt.marginOpen !== 'undefined') {
      params = { action: 'ENABLE' }
    }
    const response = await fetchChangeMargin(params);
    if (!response.success) {
      message.error(I18n.t(response.msgKey))
      return Promise.reject(new Error(response.msgKey))
    }
    message.success(I18n.t('marginTurnedOn'))
  }

  const createTradeOrder = async (params) => {
    setLoading(true);
    const res = await SpotTradeAPI.tradeAcquireOrderId();
    const orderId = res?.obj?.id;
    const marginSelected = form.getFieldValue('marginSelected')
    const type = marginBalance?.()?.openMargin && marginSelected ? 'margin' : 'spot';
    const symbol = coinDetail?.coinCode;
    const currency = symbol.split('_')[0];
    const quoteCurrency = symbol.split('_')[1];

    const req = {
      saas_id: 'kiki',
      order_id: orderId,
      base_currency: currency,
      quote_currency: quoteCurrency,
      base_qty: form.getFieldValue('baseQty'),
      quote_qty: '',
      business_type: 'trading',
      type,
      ...params
    };
    const response = await SpotTradeAPI.createTradeOrder(req);
    if (response && response.success) {
      message.success(I18n.t('market_order_success'))
      pubsub.publish('CREATE_ORDER', response)
      pubsub.publish('TRADE_PAIRE_CURRENT')
      await Promise.allSettled([tradeStore.fetchMarginData(), tradeStore.getselectAsset()])
    } else {
      message.error(I18n.t(response.msgKey || 'margin.lock.fail'))
    }
    setLoading(false)
    close?.()
    setTimeout(() => {
      fetchMarginBalance()
    }, 2000)
    form.resetFields()
  }

  // 用户点了确认之后，去开启杠杆
  const fetchEnableMargin = async ({ close, resolve, reject }) => {
    try {
      await changeMargin({ marginOpen: true })
      resolve('ok')
    } catch (error) {
      reject(error)
    }
    close()
  }

  // 当杠杆开关触动时执行 基础方法
  const publicMarginSelected = async ({ checked, isSpotAvblGreaterThanMaxAvbl, onSlider }) => {
    const progress = form.getFieldValue('progress');
    if (marginBalance?.()?.openMargin) {
      form.setFieldsValue({ marginSelected: checked })
      onSlider(progress)
      return
    }
    // 如果杠杆没有开启，并且现货余额大于最大可用 并且对应弹窗之前没有弹出过 则需要弹窗用户知晓杠杆不能开启的原因
    if (!marginBalance?.()?.openMargin && isSpotAvblGreaterThanMaxAvbl && !getIsTouchDisabledModal()) {
      await disabledOpenMargin()
      // 用户点击知道了之后，存储一下对应值，之后这个弹窗不在提示
      setIsTouchDisabledModal()
      return
    }
    if (!marginBalance?.()?.openMargin) {
      try {
        const key = await openMargin({
          callback: fetchEnableMargin
        })
        if (key !== 'ok') {
          return
        }
        // 杠杆开启之后，需要重新获取下用户杠杆配置
        await fetchMarginBalance()
        // 同时让用户使用杠杆
        form.setFieldsValue({ marginSelected: checked })
        onSlider(progress)
      } catch (error) {

      }
    }
  }

  const jumpLogin = () => {
    router.push(getLoginPath())
  }

  useIsomorphicLayoutEffect(() => {
    initPrice()
  }, [isLimit, coinDetail])

  return {
    form,
    updateBaseQty,
    handleMarginDisabled,
    updateOrderTotal,
    updatePrice,
    updateSlider,
    publicMarginSelected,
    loading,
    createTradeOrder,
    jumpLogin
  }
}
